import { snakeCase, mapKeys } from "lodash";
import qs from "query-string";

import { apiBase } from "../../../utils/apiBase";
import { DTYPES } from "./constants";

const LAYER_ID = "wells";

const SOURCE_BHL_ID = "enverus_well_bhl";
const SOURCE_SHL_ID = "enverus_well_shl";
const SOURCE_FTP_ID = "enverus_well_ftp";
const SOURCE_LATERAL_ID = "enverus_well_laterals";
const SOURCE_ANNOTATION_ID = "enverus_well_annotations";

const SOURCE_LAYER_BHL = "enverus_well_bhl";
const SOURCE_LAYER_SHL = "enverus_well_shl";
const SOURCE_LAYER_FTP = "enverus_well_ftp";
const SOURCE_LAYER_LATERAL = "enverus_well_laterals";

const LAYER_BHL_ID = "Bottoms";
const LAYER_SHL_ID = "Surface";
const LAYER_FTP_ID = "FTP";
const LAYER_LATERAL_ID = "Laterals";
const LAYER_ANNOTATION_ID = "well_annotations";

const LAYER_POTENTIAL_BHL_ID = "Bottoms_potential";
const LAYER_POTENTIAL_SHL_ID = "Surface_potential";
const LAYER_POTENTIAL_LATERAL_ID = "Laterals_potential";

const BORDER_HIGHLIGHT_LAYER_ID = "well_bottoms_highlight";
const LATERAL_HIGHLIGHT_LAYER_ID = "well_laterals_highlight";
// A highlighted well is drawn black on both its bottom-hole point and its
// lateral line. The generic highlight machinery (setHighlightFilters /
// removeHighlightFilters) applies the api_uwi_10 filter to every layer here.
const HIGHLIGHT_LAYER_IDS = [
  BORDER_HIGHLIGHT_LAYER_ID,
  LATERAL_HIGHLIGHT_LAYER_ID,
];
// well.id (exposed as `well_id` on every tile feature) is the canonical well
// identity used for highlighting/click — unlike api_uwi_10 it's present on
// potential (PUD) wells too, so highlighting works for every well.
const UNIQUE_COLUMN = "well_id";

const WELLS_LAYER_IDS = [
  LAYER_BHL_ID,
  LAYER_SHL_ID,
  LAYER_FTP_ID,
  LAYER_LATERAL_ID,
  LAYER_POTENTIAL_BHL_ID,
  LAYER_POTENTIAL_SHL_ID,
  LAYER_POTENTIAL_LATERAL_ID,
];
const FILTER_LAYER_IDS = [...WELLS_LAYER_IDS];
const CLICKABLE_LAYERS = [
  LAYER_BHL_ID,
  LAYER_SHL_ID,
  LAYER_LATERAL_ID,
  LAYER_POTENTIAL_BHL_ID,
  LAYER_POTENTIAL_SHL_ID,
  LAYER_POTENTIAL_LATERAL_ID,
];

const API_BASE_ENDPOINT = "wells";

const ADD_BEFORE = "Enverus grid sections";

/** Wells tiles are cached by the server-side proxy (nginx) with a key that
 * includes this `cacheBuster`. To keep that cache SHARED across users it is set
 * to a server value (GET /api/tiles/version) that is identical for everyone and
 * only changes when a potential well is created/edited/deleted — so the proxy
 * cache is shared yet busts immediately after an edit (the server bumps the
 * version). `refreshTiles` fetches it on map load and after edits. Until then
 * it is null and omitted from the URL (the proxy TTL still bounds staleness).
 *
 * `tileParams` are per-page query params (e.g. deleted_well_ids) that a page
 * applies via setTiles; they are part of the nginx cache key too, so pages
 * that surface deleted wells never share cache entries with default tiles.
 */
let cacheBuster = null;
let tileParams = {};

const buildTileUrl = (subpath, params = {}) => {
  const authToken = localStorage.getItem("authToken");
  const snakeParams = mapKeys({ cacheBuster, ...params }, (_, k) =>
    snakeCase(k)
  );
  const queryString = qs.stringify(snakeParams, {
    skipNull: true,
    skipEmptyString: true,
    arrayFormat: "comma",
  });
  const suffix = queryString ? `&${queryString}` : "";

  return `${process.env.REACT_APP_SERVER_URL}/api/${API_BASE_ENDPOINT}/${subpath}/tiles/{z}/{x}/{y}.pbf?access_token=${authToken}${suffix}`;
};

const SOURCE_DEFINITIONS = [
  [SOURCE_BHL_ID, "bhl"],
  [SOURCE_SHL_ID, "shl"],
  [SOURCE_FTP_ID, "ftp"],
  [SOURCE_LATERAL_ID, "laterals"],
  [SOURCE_ANNOTATION_ID, "annotations"],
];

/** Rebuild the tile URLs of every wells source. `options` semantics:
 * undefined clears the stored tile params; `{ cacheBuster }` alone is a
 * refresh that keeps them; anything else replaces them (an included
 * cacheBuster is applied too). */
const setTiles = (mapRef, options) => {
  if (options === undefined) {
    tileParams = {};
  } else {
    const { cacheBuster: nextCacheBuster, ...params } = options;

    // Presence check, not truthiness — the server tile version starts at 0
    // (fresh Redis), and {cacheBuster: 0} must still count as a refresh that
    // keeps the page's params.
    if (nextCacheBuster != null) {
      cacheBuster = nextCacheBuster;
    }
    if (Object.keys(params).length > 0 || nextCacheBuster == null) {
      tileParams = params;
    }
  }

  const map = mapRef.current;

  if (!map) return;

  SOURCE_DEFINITIONS.forEach(([sourceId, subpath]) => {
    const source = map.getSource(sourceId);

    if (source) {
      source.setTiles([buildTileUrl(subpath, tileParams)]);
    }
  });
};

/** Fetch the shared wells tile version and apply it, so the proxy cache key is
 * the same for every user (good sharing) yet busts the moment a potential well
 * is created/edited/deleted. Call on map load and after potential-well edits.
 * Fails soft: on error the tiles keep their current cacheBuster and the proxy
 * TTL still bounds staleness — never block the map on this fetch. */
const refreshTiles = async (mapRef) => {
  try {
    const { data } = await apiBase.get("/tiles/version");
    if (data?.version == null) return;
    setTiles(mapRef, { cacheBuster: data.version });
  } catch (e) {
    // Leave tiles as-is; the proxy TTL handles invalidation in the meantime.
  }
};

const isAsDrilled = (well) => {
  if (!well) return false;

  const enverusWells = well.enverus_wells || [];
  const potentialWell = well.potential_well;

  if (!potentialWell) {
    return enverusWells.length > 0;
  }

  return enverusWells.some((ew) => !!ew.has_digitized_trajectory);
};

const INITIAL_FILTER_STATE = {
  well_id: {
    column: "well_id",
    label: "ID",
    dtype: DTYPES.INTEGER,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  state: {
    column: "state",
    label: "State",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  county: {
    column: "county",
    label: "County",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  name: {
    column: "name",
    label: "Name",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  lease: {
    column: "lease",
    label: "Lease",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  number: {
    column: "number",
    label: "Number",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  operator_name: {
    column: "operator_name",
    label: "Operator",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  api_uwi_10: {
    column: "api_uwi_10",
    label: "API 10",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  rrc_id: {
    column: "rrc_id",
    label: "RRC ID",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  status: {
    column: "status",
    label: "Status",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
  stage: {
    column: "stage",
    label: "Stage",
    dtype: DTYPES.STRING,
    value: [],
    presetValue: [
      { value: "PUD", label: "PUD" },
      { value: "EXPIRED", label: "EXPIRED" },
    ],
    presetExcludeSelected: true,
    excludeSelected: false,
    excludeNullValues: false,
  },
  spud_date: {
    column: "spud_date",
    label: "Spud date",
    dtype: DTYPES.DATE,
    value: {},
    excludeSelected: false,
    excludeNullValues: false,
  },
  completion_date: {
    column: "completion_date",
    label: "Completion date",
    dtype: DTYPES.DATE,
    value: {},
    excludeSelected: false,
    excludeNullValues: false,
  },
  last_production_date: {
    column: "last_production_date",
    label: "Last production date",
    dtype: DTYPES.DATE,
    value: {},
    excludeSelected: false,
    excludeNullValues: false,
  },
  drill_type: {
    column: "drill_type",
    label: "Drill type",
    dtype: DTYPES.STRING,
    value: [],
    excludeSelected: false,
    excludeNullValues: false,
  },
};

const EXPORT_COLUMNS = [
  { key: "well_id", label: "ID" },
  { key: "api_uwi_10", label: "API 10" },
  { key: "rrc_id", label: "RRC ID" },
  { key: "name", label: "Name" },
  { key: "lease", label: "Lease" },
  { key: "number", label: "Number" },
  { key: "operator_name", label: "Operator" },
  { key: "state", label: "State" },
  { key: "county", label: "County" },
  { key: "status", label: "Status" },
  { key: "type", label: "Type" },
  { key: "spud_date", label: "Spud Date", type: "date", format: "YYYY-MM-DD" },
  {
    key: "completion_date",
    label: "Completion Date",
    type: "date",
    format: "YYYY-MM-DD",
  },
  {
    key: "first_production_date",
    label: "First Production Date",
    type: "date",
    format: "YYYY-MM-DD",
  },
  {
    key: "last_production_date",
    label: "Last Production Date",
    type: "date",
    format: "YYYY-MM-DD",
  },
  { key: "drill_type", label: "Drill Type" },
];

const WELL_TYPE_COLOR = [
  "match",
  ["get", "type"],
  ["GAS"],
  "#ff0900",
  ["OIL"],
  "#6ef54d",
  ["WATER"],
  "#0066ff",
  ["DRY HOLE"],
  "#ff00ff",
  "#0ad80a",
];

const LATERALS_PAINT = {
  "line-color": WELL_TYPE_COLOR,
  "line-dasharray": [
    "case",
    ["!", ["coalesce", ["get", "has_digitized_trajectory"], false]],
    ["literal", [5, 2]],
    ["literal", [1, 0]],
  ],
  "line-width": ["step", ["zoom"], 1, 12, 1.5, 14, 2],
};

// Potential-well sticks are coloured by stage: orange for PUD (drawn, no
// permit yet), blue for DUC (drilled, not producing), cyan for anything
// else. A well that has produced (PDP/INACTIVE) but has no surveyed Enverus
// trajectory keeps its hand-drawn stick, so it is coloured by `type` like the
// "enverus" layers (see LATERALS_PAINT). `stage` and `type` are carried on
// each tile feature.
const PUD_LATERAL_COLOR = "#F08418"; // orange
const DUC_LATERAL_COLOR = "#4672F5"; // blue
const PERMIT_LATERAL_COLOR = "#00BCD4"; // cyan

const PUD_STAGE_COLOR = [
  "match",
  ["get", "stage"],
  "PUD",
  PUD_LATERAL_COLOR,
  "DUC",
  DUC_LATERAL_COLOR,
  ["PDP", "INACTIVE"],
  WELL_TYPE_COLOR,
  PERMIT_LATERAL_COLOR,
];

const POTENTIAL_LATERAL_PAINT = {
  "line-color": PUD_STAGE_COLOR,
  "line-width": 2,
  "line-dasharray": [2, 1],
};

const POTENTIAL_POINT_PAINT = {
  "circle-color": PUD_STAGE_COLOR,
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 2, 13, 3, 16, 4],
  "circle-stroke-color": "#000",
  "circle-stroke-width": 1,
};

const FTP_POINT_PAINT = {
  "circle-color": "rgba(0, 0, 0, 0)",
  "circle-stroke-color": WELL_TYPE_COLOR,
  "circle-stroke-width": 1.5,
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 13, 1.5, 16, 2, 18, 3],
};

const BHL_LAYOUT = {
  "icon-image": ["to-string", ["get", "symbol"]],
  "icon-allow-overlap": true,
  "icon-size": ["step", ["zoom"], 0.1, 10, 0.15, 12, 0.2, 13, 0.25, 14, 0.3],
};

const SHL_LAYOUT = {
  "icon-image": ["to-string", ["get", "symbol"]],
  "icon-allow-overlap": true,
  "icon-size": ["step", ["zoom"], 0.1, 10, 0.15, 12, 0.2],
};

const POTENTIAL_SHL_LAYOUT = {
  "icon-image": [
    "match",
    ["get", "stage"],
    "PUD",
    "pud-surface",
    "DUC",
    "duc-surface",
    ["PDP", "INACTIVE"],
    [
      "match",
      ["get", "type"],
      ["OIL", "OIL & GAS"],
      "oil-surface",
      "GAS",
      "gas-surface",
      "location-surface",
    ],
    "permit-surface",
  ],
  "icon-allow-overlap": true,
  "icon-size": ["step", ["zoom"], 0.1, 10, 0.15, 12, 0.2],
};

const ANNOTATION_STYLES = {
  id: LAYER_ANNOTATION_ID,
  minzoom: 13,
  type: "symbol",
  source: SOURCE_ANNOTATION_ID,
  "source-layer": SOURCE_ANNOTATION_ID,
  layout: {
    "text-size": 10,
    "text-font": ["Prompt Regular", "Arial Unicode MS Regular"],
    "text-padding": 0,
    "text-offset": [0, 1.5],
    "text-field": ["to-string", ["get", "name"]],
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-justify": "auto",
    "text-variable-anchor": ["top", "bottom"],
    "symbol-sort-key": ["get", "id"],
    visibility: "visible",
  },
  paint: {
    "text-halo-color": "#ffffff",
    "text-halo-width": 1,
    "symbol-z-offset": 0,
  },
};

const BORDER_HIGHLIGHT_STYLES = {
  "circle-color": "#000",
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 4, 13, 6, 16, 8],
};

// A black casing wider than the normal lateral (LATERALS_PAINT line-width
// peaks at 2), drawn beneath the coloured lateral so it reads as a bold black
// outline while keeping the oil/gas colour visible.
const LATERAL_HIGHLIGHT_STYLES = {
  "line-color": "#000",
  "line-width": ["step", ["zoom"], 2.5, 12, 4, 14, 5.5],
  "line-cap": "round",
};

const SOURCE_FILTER_ENVERUS = ["==", "source", "enverus"];
const SOURCE_FILTER_POTENTIAL = ["==", "source", "potential"];

const LAYER_BASE_FILTERS = {
  [LAYER_BHL_ID]: SOURCE_FILTER_ENVERUS,
  [LAYER_SHL_ID]: SOURCE_FILTER_ENVERUS,
  [LAYER_FTP_ID]: SOURCE_FILTER_ENVERUS,
  [LAYER_LATERAL_ID]: SOURCE_FILTER_ENVERUS,
  [LAYER_POTENTIAL_BHL_ID]: SOURCE_FILTER_POTENTIAL,
  [LAYER_POTENTIAL_SHL_ID]: SOURCE_FILTER_POTENTIAL,
  [LAYER_POTENTIAL_LATERAL_ID]: SOURCE_FILTER_POTENTIAL,
};

const addSources = (mapRef) => {
  const map = mapRef.current;

  // A new map starts from default tiles — drop params left over from a
  // previously mounted map page; its own page re-applies them via setTiles.
  tileParams = {};

  SOURCE_DEFINITIONS.forEach(([sourceId, subpath]) => {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "vector",
        tiles: [buildTileUrl(subpath)],
        minzoom: 5,
        maxzoom: 22,
      });
    }
  });
};

const addLayers = (mapRef) => {
  const map = mapRef.current;

  if (!map.getLayer(LAYER_LATERAL_ID)) {
    map.addLayer(
      {
        id: LAYER_LATERAL_ID,
        type: "line",
        source: SOURCE_LATERAL_ID,
        "source-layer": SOURCE_LAYER_LATERAL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_LATERAL_ID],
        paint: LATERALS_PAINT,
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_POTENTIAL_LATERAL_ID)) {
    map.addLayer(
      {
        id: LAYER_POTENTIAL_LATERAL_ID,
        type: "line",
        source: SOURCE_LATERAL_ID,
        "source-layer": SOURCE_LAYER_LATERAL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_POTENTIAL_LATERAL_ID],
        paint: POTENTIAL_LATERAL_PAINT,
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_SHL_ID)) {
    map.addLayer(
      {
        id: LAYER_SHL_ID,
        type: "symbol",
        source: SOURCE_SHL_ID,
        "source-layer": SOURCE_LAYER_SHL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_SHL_ID],
        layout: SHL_LAYOUT,
        paint: {},
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_FTP_ID)) {
    map.addLayer(
      {
        id: LAYER_FTP_ID,
        type: "circle",
        source: SOURCE_FTP_ID,
        "source-layer": SOURCE_LAYER_FTP,
        minzoom: 13,
        filter: SOURCE_FILTER_ENVERUS,
        paint: FTP_POINT_PAINT,
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_POTENTIAL_SHL_ID)) {
    map.addLayer(
      {
        id: LAYER_POTENTIAL_SHL_ID,
        type: "symbol",
        source: SOURCE_SHL_ID,
        "source-layer": SOURCE_LAYER_SHL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_POTENTIAL_SHL_ID],
        layout: POTENTIAL_SHL_LAYOUT,
        paint: {},
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_BHL_ID)) {
    map.addLayer(
      {
        id: LAYER_BHL_ID,
        type: "symbol",
        source: SOURCE_BHL_ID,
        "source-layer": SOURCE_LAYER_BHL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_BHL_ID],
        layout: BHL_LAYOUT,
        paint: {},
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_POTENTIAL_BHL_ID)) {
    map.addLayer(
      {
        id: LAYER_POTENTIAL_BHL_ID,
        type: "circle",
        source: SOURCE_BHL_ID,
        "source-layer": SOURCE_LAYER_BHL,
        minzoom: 5,
        filter: LAYER_BASE_FILTERS[LAYER_POTENTIAL_BHL_ID],
        paint: POTENTIAL_POINT_PAINT,
      },
      ADD_BEFORE
    );
  }

  if (!map.getLayer(LAYER_ANNOTATION_ID)) {
    map.addLayer(ANNOTATION_STYLES, ADD_BEFORE);
  }

  if (!map.getLayer(BORDER_HIGHLIGHT_LAYER_ID)) {
    map.addLayer(
      {
        id: BORDER_HIGHLIGHT_LAYER_ID,
        type: "circle",
        source: SOURCE_BHL_ID,
        "source-layer": SOURCE_LAYER_BHL,
        layout: {},
        paint: BORDER_HIGHLIGHT_STYLES,
        filter: ["in", UNIQUE_COLUMN, ""],
      },
      LAYER_BHL_ID
    );
  }

  if (!map.getLayer(LATERAL_HIGHLIGHT_LAYER_ID)) {
    map.addLayer(
      {
        id: LATERAL_HIGHLIGHT_LAYER_ID,
        type: "line",
        source: SOURCE_LATERAL_ID,
        "source-layer": SOURCE_LAYER_LATERAL,
        layout: {},
        paint: LATERAL_HIGHLIGHT_STYLES,
        filter: ["in", UNIQUE_COLUMN, ""],
      },
      // Beneath the coloured laterals so the highlight reads as a casing.
      LAYER_LATERAL_ID
    );
  }
};

const addAll = (mapRef) => {
  addSources(mapRef);
  addLayers(mapRef);
  // Apply the shared tile version once the sources exist (fire-and-forget).
  refreshTiles(mapRef);
};

const setAnnotationVisibility = (action) => {
  const { mapRef, value } = action;

  if (!mapRef || !mapRef.current) return;

  const map = mapRef.current;

  if (map.getLayer(LAYER_ANNOTATION_ID)) {
    const visibility = value ? "visible" : "none";
    const textVariableAnchor = value ? ["top", "bottom"] : ["bottom"];
    map.setLayoutProperty(LAYER_ANNOTATION_ID, "visibility", visibility);
    map.setLayoutProperty(
      LAYER_ANNOTATION_ID,
      "text-variable-anchor",
      textVariableAnchor
    );
  }
};

export {
  EXPORT_COLUMNS,
  LAYER_ID,
  BORDER_HIGHLIGHT_LAYER_ID,
  LATERAL_HIGHLIGHT_LAYER_ID,
  HIGHLIGHT_LAYER_IDS,
  LAYER_ANNOTATION_ID,
  LAYER_BHL_ID,
  LAYER_SHL_ID,
  LAYER_FTP_ID,
  LAYER_LATERAL_ID,
  LAYER_POTENTIAL_BHL_ID,
  LAYER_POTENTIAL_SHL_ID,
  LAYER_POTENTIAL_LATERAL_ID,
  FILTER_LAYER_IDS,
  WELLS_LAYER_IDS,
  CLICKABLE_LAYERS,
  LAYER_BASE_FILTERS,
  API_BASE_ENDPOINT,
  INITIAL_FILTER_STATE,
  UNIQUE_COLUMN,
  addAll,
  setAnnotationVisibility,
  setTiles,
  refreshTiles,
  isAsDrilled,
};
